import React, { useState, useEffect, useId } from 'react';
import { Mail } from 'lucide-react';

interface EmailInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  label?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  helperText?: string;
}

export function EmailInput({
  value,
  onChange,
  onBlur,
  label,
  error,
  required = false,
  disabled = false,
  placeholder = "votre@email.com",
  className = '',
  helperText
}: EmailInputProps) {
  
  const id = useId();
  const [isValidEmail, setIsValidEmail] = useState(false);
  
  // Validation email en temps réel
  useEffect(() => {
    if (value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setIsValidEmail(emailRegex.test(value));
    } else {
      setIsValidEmail(false);
    }
  }, [value]);

  // Suggestions de domaines populaires
  const getEmailSuggestion = (email: string): string | null => {
    const commonDomains = [
      'gmail.com', 'outlook.com', 'hotmail.com', 'yahoo.fr', 'orange.fr', 
      'free.fr', 'wanadoo.fr', 'sfr.fr', 'laposte.net', 'icloud.com'
    ];
    
    if (!email.includes('@') || email.split('@').length !== 2) return null;
    
    const [localPart, domainPart] = email.split('@');
    if (!domainPart || domainPart.length < 2) return null;
    
    // Chercher une correspondance proche
    const suggestion = commonDomains.find(domain => 
      domain.toLowerCase().startsWith(domainPart.toLowerCase()) &&
      domain !== domainPart.toLowerCase()
    );
    
    return suggestion ? `${localPart}@${suggestion}` : null;
  };

  const emailSuggestion = getEmailSuggestion(value);

  const handleSuggestionClick = () => {
    if (emailSuggestion) {
      const event = {
        target: { value: emailSuggestion }
      } as React.ChangeEvent<HTMLInputElement>;
      onChange(event);
    }
  };

  const inputClasses = `input-field pl-10 pr-10 ${error ? 'input-error' : ''} ${className}`;

  return (
    <div className="field-group">
      {label && (
        <label htmlFor={id} className="form-label">
          {label}
          {required && <span className="form-label-required">*</span>}
        </label>
      )}

      <div className="relative">
        <input
          id={id}
          type="email"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className={`${inputClasses} focus-visible`}
          disabled={disabled}
          autoComplete="email"
          spellCheck={false}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none transition-colors duration-200">
          <Mail className={`h-4 w-4 ${error ? 'text-error-500' : 'text-neutral-400'}`} />
        </div>

      </div>

      {/* Email suggestion */}
      {emailSuggestion && !error && (
        <div className="flex items-center gap-2 text-xs animate-slide-up">
          <span className="text-neutral-500">Vouliez-vous dire :</span>
          <button
            type="button"
            onClick={handleSuggestionClick}
            className="text-primary-600 hover:text-primary-700 underline font-medium transition-colors"
          >
            {emailSuggestion}
          </button>
        </div>
      )}

      {helperText && !error && !emailSuggestion && <p className="helper-text">{helperText}</p>}

      {error && <p id={`${id}-error`} className="error-message">{error}</p>}

      {/* Invalid format indicator */}
      {value && !isValidEmail && !error && value.length > 3 && (
        <p className="text-xs text-warning-700 animate-fade-in">Format d'email invalide</p>
      )}
    </div>
  );
}